'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useWallet as useWalletHook } from './use-wallet';
import { useConnectorClient } from 'wagmi';
import { type Hex } from 'viem';
import { clientChain, clientPublicClient } from '@/lib/viem-client';

const chain = clientChain;
const hexChainId = `0x${chain.id.toString(16)}`;

// =============================================================================
// ERC-5792 Helpers (wallet_sendCalls / wallet_getCallsStatus)
// =============================================================================

// MetaMask (and potentially other wallets) cap batch size at 10.
const MAX_BATCH_SIZE = 10;

type EIP1193Provider = { request: (args: { method: string; params?: unknown[] }) => Promise<unknown> };

// Wrap an `eth_sendTransaction` / `wallet_sendCalls` request with a defensive
// timeout. Without this a broken/unresponsive wallet provider (stale proxy,
// mismatched chainId, iframe lost focus, etc.) can cause the promise to
// never settle, leaving the snackbar stuck on "pending" with no console
// output at all — the exact failure mode we were debugging.
const WALLET_PROMPT_TIMEOUT_MS = 120_000;

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`${label} did not respond within ${ms / 1000}s — wallet may be stuck, reload the page and reconnect`)),
        ms,
      ),
    ),
  ]);
}

/** Send multiple calls as a single batch via ERC-5792 wallet_sendCalls.
 *  Returns the batch identifier string for use with wallet_getCallsStatus.
 *  ERC-5792 v1 returns a plain string; v2 returns { id: string, ... }. */
async function sendCallsBatch(
  provider: EIP1193Provider,
  walletAddress: string,
  calls: TransactionCall[],
): Promise<string> {
  console.info('[tx] wallet_sendCalls →', { from: walletAddress, chainId: hexChainId, callCount: calls.length });
  const response = await withTimeout(
    provider.request({
      method: 'wallet_sendCalls',
      params: [{
        version: '2.0.0',
        chainId: hexChainId,
        from: walletAddress,
        atomicRequired: true,
        calls: calls.map(c => ({
          to: c.to,
          data: c.data || '0x',
          value: c.value ? `0x${c.value.toString(16)}` : '0x0',
        })),
      }],
    }),
    WALLET_PROMPT_TIMEOUT_MS,
    'wallet_sendCalls',
  );
  console.info('[tx] wallet_sendCalls ←', response);

  // v1 returns a plain string batch ID; v2 returns an object with an `id` field
  if (typeof response === 'string') return response;
  if (response && typeof response === 'object' && 'id' in response) {
    return (response as { id: string }).id;
  }
  console.warn('[ERC-5792] Unexpected wallet_sendCalls response format:', response);
  return String(response);
}

// =============================================================================
// ERC-5792 capability probe
//
// Some wallets (notably MetaMask in certain keyring/vault states) will accept
// a `wallet_sendCalls` request and then throw a `KeyringController` error
// while servicing it — leaving the user with N opaque "Batch failed" rows in
// the snackbar and no successful retry path. To avoid even attempting a
// batched send on a wallet that doesn't actually support atomic execution
// on the current chain, we call `wallet_getCapabilities` ONCE per session
// (per wallet + chain) and cache the answer.
//
// Per EIP-5792, an `atomic.status` of `'supported'` or `'ready'` means the
// wallet can fulfil `atomicRequired: true`. `'unsupported'`, a missing key,
// an empty map, or an outright throw are all treated as "not supported" —
// we fall back to sequential `eth_sendTransaction` in those cases.
//
// We deliberately keep this cache module-level (not stateful) so it survives
// drawer remounts and React Strict Mode double-effects. The cache is keyed on
// `${address.toLowerCase()}:${chainIdHex}` so multi-chain / wallet-switch
// scenarios re-probe correctly.
// =============================================================================

const batchSupportCache = new Map<string, boolean>();

interface AtomicCapability {
  status?: 'supported' | 'ready' | 'unsupported' | string;
}

interface ChainCapabilityMap {
  atomic?: AtomicCapability;
  atomicBatch?: { supported?: boolean };
  [k: string]: unknown;
}

function capabilityKey(address: string, chainIdHex: string): string {
  return `${address.toLowerCase()}:${chainIdHex.toLowerCase()}`;
}

/** Probe `wallet_getCapabilities` to determine whether the connected wallet
 *  can service an atomic `wallet_sendCalls` batch on the active chain.
 *  Memoised per (address, chain) for the lifetime of the page. */
async function probeBatchingSupport(
  provider: EIP1193Provider,
  walletAddress: string,
  chainIdHex: string,
): Promise<boolean> {
  const key = capabilityKey(walletAddress, chainIdHex);
  const cached = batchSupportCache.get(key);
  if (cached !== undefined) return cached;

  let supported = false;
  try {
    const raw = await Promise.race([
      provider.request({
        method: 'wallet_getCapabilities',
        params: [walletAddress, [chainIdHex]],
      }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('capabilities_probe_timeout')), 5_000),
      ),
    ]);

    const caps = raw as Record<string, ChainCapabilityMap> | null | undefined;
    const map = caps
      ? caps[chainIdHex] ?? caps[chainIdHex.toLowerCase()] ?? caps[chainIdHex.toUpperCase()]
      : undefined;
    if (map) {
      const status = map.atomic?.status;
      if (status === 'supported' || status === 'ready') {
        supported = true;
        if (status === 'ready') {
          // 'ready' means the wallet would upgrade the EOA to a 7702 smart
          // account on first send. That's the path most associated with the
          // KeyringController collisions in the wild; we still allow it, but
          // the auto-fallback below will catch it if the upgrade itself fails.
          console.info('[ERC-5792] Capability status=ready (wallet may 7702-upgrade on first send)');
        }
      } else if (map.atomicBatch?.supported === true) {
        // EIP-5792 v1 legacy shape — also a positive signal.
        supported = true;
      }
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.info('[ERC-5792] wallet_getCapabilities probe failed — assuming sequential:', msg);
    supported = false;
  }

  batchSupportCache.set(key, supported);
  console.info(`[ERC-5792] batching supported for ${key} = ${supported}`);
  return supported;
}

interface ERC5792Receipt {
  transactionHash: string;
  status: string;
  blockHash: string;
  blockNumber: string;
  gasUsed: string;
  logs: Array<{ address: string; data: string; topics: string[] }>;
}

interface ERC5792CallsStatus {
  status: unknown;
  receipts?: ERC5792Receipt[];
}

/** Normalize wallet_getCallsStatus `status` to a number.
 *  Handles: number 200, hex "0xc8", decimal string "200", label "CONFIRMED". */
function normalizeStatus(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    if (raw.startsWith('0x')) return parseInt(raw, 16);
    const n = Number(raw);
    if (!isNaN(n)) return n;
    const map: Record<string, number> = { PENDING: 100, CONFIRMED: 200, FAILED: 300 };
    return map[raw.toUpperCase()] ?? -1;
  }
  return -1;
}

/** Check whether a receipt status value indicates success. */
function isReceiptSuccess(status: unknown): boolean {
  if (!status) return true; // falsy status treated as success (field may be absent)
  if (status === '0x1' || status === '0x01' || status === '1') return true;
  if (status === 'success' || status === true) return true;
  if (typeof status === 'number') return status === 1;
  return false;
}

const POLL_REQUEST_TIMEOUT = 10_000;
const MAX_CONSECUTIVE_ERRORS = 5;

/** Poll wallet_getCallsStatus until the batch is confirmed or times out.
 *  ERC-5792 statuses: 100 = pending, 200 = confirmed, 300+ = failed/reverted.
 *
 *  Falls back to nonce-based confirmation when the wallet doesn't respond
 *  properly to wallet_getCallsStatus (e.g. Base wallet in-app browser). */
async function pollCallsStatus(
  provider: EIP1193Provider,
  batchId: string,
  client: { getTransactionCount: (args: { address: `0x${string}` }) => Promise<number> },
  walletAddress: string,
  timeoutMs = 120_000,
  nonceBefore?: number,
): Promise<{ hash: Hex; receipt: ERC5792Receipt }> {
  const startTime = Date.now();
  const pollInterval = 2000;
  let consecutiveErrors = 0;

  while (Date.now() - startTime < timeoutMs) {
    // --- wallet_getCallsStatus poll (with per-request timeout) ---
    try {
      const raw = await Promise.race([
        provider.request({ method: 'wallet_getCallsStatus', params: [batchId] }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('poll_timeout')), POLL_REQUEST_TIMEOUT),
        ),
      ]);

      consecutiveErrors = 0;
      const result = raw as ERC5792CallsStatus;
      const status = normalizeStatus(result.status);

      if (status === 200) {
        const receipt = result.receipts?.[0];
        if (receipt && !isReceiptSuccess(receipt.status)) {
          throw new Error('Batch transaction reverted onchain');
        }
        return {
          hash: (receipt?.transactionHash ?? '0x') as Hex,
          receipt: receipt ?? {} as ERC5792Receipt,
        };
      }

      if (status >= 300) {
        const receipt = result.receipts?.[0];
        const hash = receipt?.transactionHash || '';
        throw new Error(
          `Batch transaction failed (status ${status})${hash ? `. Tx: ${hash}` : ''}`
        );
      }
    } catch (err) {
      if (err instanceof Error && (
        err.message.includes('Batch transaction') ||
        err.message.includes('reverted')
      )) {
        throw err;
      }

      const errCode = err && typeof err === 'object' && 'code' in err
        ? (err as { code: number }).code : undefined;
      const errMsg = err instanceof Error ? err.message : String(err);
      if (
        errCode === 5730 ||
        errMsg.includes('No matching bundle') ||
        errMsg.includes('rejected') ||
        errMsg.includes('denied') ||
        errMsg.includes('cancelled') ||
        errMsg.includes('canceled') ||
        errMsg.includes('User refused')
      ) {
        throw new Error('Transaction rejected');
      }

      if (errMsg !== 'poll_timeout') {
        consecutiveErrors++;
        if (consecutiveErrors === 1) {
          console.warn('[ERC-5792] wallet_getCallsStatus poll error:', err);
        }
      }
    }

    // --- Nonce-based fallback: if wallet_getCallsStatus isn't working,
    //     detect confirmation by checking if the account nonce increased. ---
    if (nonceBefore !== undefined && consecutiveErrors >= 2) {
      try {
        const currentNonce = await client.getTransactionCount({ address: walletAddress as `0x${string}` });
        if (currentNonce > nonceBefore) {
          console.info('[ERC-5792] Nonce increased — batch confirmed onchain despite wallet_getCallsStatus failure');
          return { hash: '0x' as Hex, receipt: {} as ERC5792Receipt };
        }
      } catch { /* non-critical */ }
    }

    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      // wallet_getCallsStatus is consistently failing; do one final nonce check
      if (nonceBefore !== undefined) {
        try {
          const currentNonce = await client.getTransactionCount({ address: walletAddress as `0x${string}` });
          if (currentNonce > nonceBefore) {
            return { hash: '0x' as Hex, receipt: {} as ERC5792Receipt };
          }
        } catch { /* non-critical */ }
      }
      throw new Error('Batch confirmation unavailable — transaction may have succeeded. Check your wallet for status.');
    }

    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }

  // Final nonce check before timeout
  if (nonceBefore !== undefined) {
    try {
      const currentNonce = await client.getTransactionCount({ address: walletAddress as `0x${string}` });
      if (currentNonce > nonceBefore) {
        return { hash: '0x' as Hex, receipt: {} as ERC5792Receipt };
      }
    } catch { /* non-critical */ }
  }

  throw new Error(`Batch transaction timeout after ${timeoutMs / 1000}s`);
}

// =============================================================================
// Types
// =============================================================================

export type StepStatus = 'waiting' | 'pending' | 'success' | 'error' | 'cancelled';

export interface ActionStep {
  id: string;
  label: string;
  description?: string;
  status: StepStatus;
  hash?: Hex;
  error?: string;
  /** Whether this step was executed as part of a batched transaction */
  batched?: boolean;
}

export interface TransactionCall {
  to: Hex;
  data?: Hex;
  value?: bigint;
}

/**
 * A call that can be provided statically or lazily (resolved at execution time).
 * Lazy calls are useful when calldata depends on the result of a previous step
 * (e.g. the tokenUri from an IPFS upload). They still participate in ERC-5792
 * batching as long as they appear consecutively with other call items.
 */
export type CallOrLazyCall = TransactionCall | (() => TransactionCall | Promise<TransactionCall>);

/**
 * A step in the action queue - can be either a transaction or an async action
 */
export interface ActionQueueItem {
  id: string;
  label: string;
  description?: string;
  /** For onchain transactions — static calldata or a lazy function resolved at execution time */
  call?: CallOrLazyCall;
  /** For async actions (like IPFS upload) - return value is passed to next step */
  action?: () => Promise<unknown>;
  /**
   * Opt this step out of ERC-5792 `wallet_sendCalls` bundling. When set,
   * the executor routes the call through the sequential `eth_sendTransaction`
   * path so it confirms as its own underlying transaction instead of being
   * collected (with `atomicRequired: true`) into a multi-call bundle.
   *
   * Used for steps whose individual gas cost is high enough that bundling
   * would overflow the wallet/bundler/RPC simulation cap — e.g. the
   * V21 `initializeSeats` batches in the launch wizard, which are ~7M gas
   * each (~30M when four batches are bundled atomically).
   *
   * Adjacent runs of `noBatch` items each execute as their own tx;
   * non-`noBatch` items either side of them still get bundled together.
   */
  noBatch?: boolean;
}

// =============================================================================
// State
// =============================================================================

export interface ActionQueueState {
  isOpen: boolean;
  title: string;
  steps: ActionStep[];
  isBatched: boolean;
  /** Number of individual transactions that were batched into one */
  batchedCount: number;
  /** Total number of wallet_sendCalls chunks (0 when not batching) */
  totalBatchChunks: number;
  /** Index of the step that failed (for retry) */
  failedAtIndex?: number;
  /** Whether a retry is in progress */
  isRetrying: boolean;
}

export interface ExecuteOptions {
  onStepComplete?: (stepId: string, result: unknown) => void;
  onSuccess?: (results: Map<string, unknown>) => void;
  onError?: (error: Error, stepId: string) => void;
}

export interface ActionQueueContextValue {
  state: ActionQueueState;
  supportsBatching: boolean;
  executeActions: (
    title: string,
    actions: ActionQueueItem[],
    options?: ExecuteOptions
  ) => Promise<{ success: boolean; results: Map<string, unknown> }>;
  retryFromFailed: () => Promise<{ success: boolean; results: Map<string, unknown> }>;
  retryAll: () => Promise<{ success: boolean; results: Map<string, unknown> }>;
  cancelQueue: () => void;
  canRetry: boolean;
  close: () => void;
  updateStep: (id: string, updates: Partial<ActionStep>) => void;
  /**
   * Warm the wallet's EIP-1193 provider in the background. Privy's
   * `wallet.getEthereumProvider()` and the wagmi connector probe both
   * lazy-init transports on the first call (40-300 ms on a cold
   * cache), and that latency lands inside the click → wallet-prompt
   * critical path. Drawers / action buttons call this when the user
   * hovers or the drawer opens so the provider is ready by the time
   * the user actually clicks submit. Safe to call repeatedly; no-op
   * if a wallet isn't connected.
   */
  prefetchProvider: () => void;
}

// =============================================================================
// Context
// =============================================================================

export const ActionQueueContext = createContext<ActionQueueContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

export function TransactionQueueProvider({ children }: { children: ReactNode }) {
  const { wallet } = useWalletHook();
  // Wagmi connector client — when an external wallet is connected via wagmi,
  // its EIP-1193 provider is reachable through `connectorClient.transport.request`.
  // For embedded Privy wallets (no wagmi connector active) we fall back to
  // `wallet.getEthereumProvider()` below.
  const { data: connectorClient } = useConnectorClient();
  const queryClient = useQueryClient();
  const [state, setState] = useState<ActionQueueState>({
    isOpen: false,
    title: '',
    steps: [],
    isBatched: false,
    batchedCount: 0,
    totalBatchChunks: 0,
    isRetrying: false,
  });

  // Refetch all USDC balances after transactions
  const refreshAllBalances = useCallback(async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const addr = wallet?.address;
    if (addr) {
      await fetch(`/api/rpc/balance/${addr}?fresh=1`).catch(() => {});
    }

    await queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'usdc-balance' ||
        query.queryKey[0] === 'usdc-balance-cached',
      refetchType: 'all',
    });
  }, [queryClient, wallet?.address]);

  // Store current actions and options for retry
  const currentActionsRef = useRef<ActionQueueItem[]>([]);
  const currentOptionsRef = useRef<ExecuteOptions | undefined>();
  const currentResultsRef = useRef<Map<string, unknown>>(new Map());
  const cancelledRef = useRef(false);

  // ERC-5792 batching — enabled for wallets that support it.
  // Privy embedded wallets don't support wallet_sendCalls.
  // Other wallets are probed at runtime; if wallet_sendCalls fails with a
  // method-not-supported error we disable batching for the rest of the session.
  const isPrivyEmbedded = wallet?.walletClientType === 'privy' || wallet?.walletClientType === 'privy-v2';
  const batchDisabledRef = useRef(false);

  // Resolve an EIP-1193 provider. Prefers Privy's wallet provider (via
  // `wallet.getEthereumProvider()`) because it is re-requested fresh on
  // every call so it always reflects the current chainId (per Privy's
  // own guidance: "This will not update any existing provider instances,
  // re-request `wallet.getEthereumProvider` to get a provider with the
  // updated chainId"). Falls back to wagmi's connector client transport
  // only when no Privy wallet is available, which covers wallet-kit
  // style setups where the user isn't logged into Privy at all.
  const getEip1193Provider = useCallback(async (): Promise<EIP1193Provider> => {
    if (wallet) {
      try {
        const provider = await wallet.getEthereumProvider();
        if (provider && typeof provider.request === 'function') return provider;
        console.warn('[tx] wallet.getEthereumProvider() returned a non-EIP1193 value:', provider);
      } catch (err) {
        console.warn('[tx] wallet.getEthereumProvider() failed, falling back to wagmi transport:', err);
      }
    }
    const transportRequest = (connectorClient?.transport as { request?: EIP1193Provider['request'] } | undefined)?.request;
    if (transportRequest) {
      return { request: (args) => transportRequest(args) };
    }
    throw new Error('No wallet connected');
  }, [connectorClient, wallet]);

  // Fire-and-forget warmer for `getEip1193Provider`. Drawers call this on
  // open so the cold-path provider init does not extend the click-to-prompt
  // latency. We deliberately swallow errors — the real call site below will
  // surface them when the user actually submits.
  const providerWarmedRef = useRef(false);
  const prefetchProvider = useCallback(() => {
    if (providerWarmedRef.current) return;
    if (!wallet && !connectorClient) return;
    providerWarmedRef.current = true;
    getEip1193Provider().catch(() => {
      // Allow a retry on the next open if the warm path failed (e.g.
      // wallet was still connecting on first call).
      providerWarmedRef.current = false;
    });
  }, [connectorClient, getEip1193Provider, wallet]);

  // Re-arm the warmer whenever the active wallet changes so a wallet swap
  // doesn't leave us holding a stale "already warmed" flag.
  useEffect(() => {
    providerWarmedRef.current = false;
  }, [wallet?.address, wallet?.walletClientType]);

  // Shared client (singleton) — `waitForTransactionReceipt` is safe to run
  // through the same batched transport as every other read.
  const publicClient = clientPublicClient;

  // Helper to update a step
  const updateStep = useCallback((id: string, updates: Partial<ActionStep>) => {
    setState(prev => ({
      ...prev,
      steps: prev.steps.map(s => s.id === id ? { ...s, ...updates } : s),
    }));
  }, []);

  // -------------------------------------------------------------------------
  // Core: wait for a tx to be mined
  // -------------------------------------------------------------------------
  const waitForReceipt = useCallback(async (
    hash: Hex,
    provider: { request: (args: { method: string; params: unknown[] }) => Promise<unknown> }
  ) => {
    const startTime = Date.now();
    const timeout = 120_000;
    const pollInterval = 2000;
    let consecutiveErrors = 0;

    while (Date.now() - startTime < timeout) {
      // Try app RPC first
      try {
        const receipt = await publicClient.getTransactionReceipt({ hash });
        if (receipt?.blockNumber) {
          const currentBlock = await publicClient.getBlockNumber();
          if (currentBlock >= receipt.blockNumber) {
            return receipt;
          }
        }
      } catch {
        consecutiveErrors++;

        // Try wallet provider as fallback after 2 failures
        if (consecutiveErrors >= 2) {
          try {
            const walletReceipt = await provider.request({
              method: 'eth_getTransactionReceipt',
              params: [hash],
            }) as {
              blockNumber: string | null;
              status: string;
              transactionHash: string;
              blockHash: string;
              gasUsed: string;
              logs: Array<{ address: string; data: string; topics: string[] }>;
            } | null;

            if (walletReceipt?.blockNumber) {
              return {
                blockNumber: BigInt(walletReceipt.blockNumber),
                status: walletReceipt.status === '0x1' ? 'success' as const : 'reverted' as const,
                transactionHash: hash,
                blockHash: walletReceipt.blockHash as Hex,
                gasUsed: BigInt(walletReceipt.gasUsed || '0'),
                logs: walletReceipt.logs?.map(log => ({
                  address: log.address as Hex,
                  data: log.data as Hex,
                  topics: log.topics as Hex[],
                })) || [],
                contractAddress: null,
                cumulativeGasUsed: BigInt(0),
                effectiveGasPrice: BigInt(0),
                from: wallet?.address as Hex,
                logsBloom: '0x' as Hex,
                to: null,
                transactionIndex: 0,
                type: 'eip1559' as const,
              } as Awaited<ReturnType<typeof publicClient.waitForTransactionReceipt>>;
            }
          } catch { /* ignore fallback failure */ }
        }
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error(`Transaction timeout after ${timeout / 1000}s. Check: https://basescan.org/tx/${hash}`);
  }, [publicClient, wallet]);

  // -------------------------------------------------------------------------
  // Core: send one transaction and wait for confirmation
  // -------------------------------------------------------------------------
  const sendAndConfirmTx = useCallback(async (
    call: TransactionCall,
    provider: { request: (args: { method: string; params: unknown[] }) => Promise<unknown> },
    walletAddress: Hex
  ) => {
    // Retry wrapper — Privy embedded wallet RPCs can transiently fail on
    // nonce lookups (eth_getTransactionCount) before the tx is submitted.
    // These pre-submission errors are safe to retry automatically.
    const MAX_SEND_RETRIES = 3;
    let hash: Hex | undefined;

    for (let attempt = 0; attempt <= MAX_SEND_RETRIES; attempt++) {
      try {
        console.info('[tx] eth_sendTransaction →', { from: walletAddress, to: call.to, attempt: attempt + 1 });
        hash = await withTimeout(
          provider.request({
            method: 'eth_sendTransaction',
            params: [{
              from: walletAddress,
              to: call.to,
              data: (call.data || '0x') as Hex,
              value: call.value ? (`0x${call.value.toString(16)}` as Hex) : ('0x0' as Hex),
              chainId: `0x${chain.id.toString(16)}`,
            }],
          }),
          WALLET_PROMPT_TIMEOUT_MS,
          'eth_sendTransaction',
        ) as Hex;
        console.info('[tx] eth_sendTransaction ←', hash);
        break; // success
      } catch (sendError: unknown) {
        const msg = sendError instanceof Error ? sendError.message : String(sendError);
        const isUserRejection =
          msg.includes('rejected') ||
          msg.includes('denied') ||
          msg.includes('cancelled') ||
          msg.includes('canceled') ||
          msg.includes('User refused');

        if (isUserRejection || attempt === MAX_SEND_RETRIES) {
          throw sendError;
        }

        // Transient RPC error — back off and retry
        console.warn(`[sendAndConfirmTx] Attempt ${attempt + 1} failed, retrying...`, msg);
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
      }
    }

    const receipt = await waitForReceipt(hash!, provider);

    if (receipt.status !== 'success') {
      throw new Error('Transaction reverted');
    }

    return { hash: hash!, receipt };
  }, [waitForReceipt]);

  // Resolve a static or lazy call into a concrete TransactionCall
  const resolveCall = useCallback(async (call: CallOrLazyCall): Promise<TransactionCall> => {
    if (typeof call === 'function') {
      return await call();
    }
    return call;
  }, []);

  // -------------------------------------------------------------------------
  // Execute from a given index (supports retry)
  // -------------------------------------------------------------------------
  const executeFromIndex = useCallback(async (
    startIndex: number,
    isRetry = false
  ): Promise<{ success: boolean; results: Map<string, unknown> }> => {
    const actions = currentActionsRef.current;
    const options = currentOptionsRef.current;
    const results = currentResultsRef.current;

    if (!wallet) {
      console.warn('[tx] executeFromIndex aborted: no wallet connected');
      options?.onError?.(new Error('No wallet connected'), 'wallet');
      return { success: false, results };
    }

    let provider: EIP1193Provider;
    try {
      provider = await getEip1193Provider();
    } catch (err) {
      console.error('[tx] executeFromIndex: failed to obtain EIP-1193 provider', err);
      // Surface the failure on the first step so the UI doesn't stay stuck
      // in "waiting" forever when the provider can't be resolved.
      const firstStep = actions[startIndex];
      if (firstStep) {
        updateStep(firstStep.id, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Wallet provider unavailable',
        });
      }
      setState(prev => ({ ...prev, failedAtIndex: startIndex, isRetrying: false }));
      options?.onError?.(
        err instanceof Error ? err : new Error('Wallet provider unavailable'),
        firstStep?.id ?? 'wallet',
      );
      return { success: false, results };
    }
    console.info('[tx] executeFromIndex: provider acquired', {
      walletAddress: wallet.address,
      walletClientType: wallet.walletClientType,
      isPrivyEmbedded,
      batchDisabled: batchDisabledRef.current,
      providerKind: provider === undefined ? 'undefined' : (provider.constructor && provider.constructor.name) || typeof provider,
    });
    const walletAddress = wallet.address as Hex;

    // Preflight chain switch — required for wallets that enforce a
    // "per-dapp-selected-network" (MetaMask, Coinbase Wallet, Rabby, …).
    // Those wallets throw "Chain ID must match the dApp selected network:
    // Got 0x2105, expected 0x1" when `eth_sendTransaction` is called with
    // a chainId that doesn't match their per-dapp record, which is what
    // happens when the user lands on the dApp while their wallet is still
    // on Ethereum mainnet and we never re-associated this origin with Base.
    //
    // Privy's `wallet.switchChain` is a no-op for embedded wallets (which
    // derive chainId from `defaultChain`) and dispatches
    // `wallet_switchEthereumChain` for external providers, matching the
    // pattern already used in `use-flaunch.ts`.
    //
    // We probe the current chain via `eth_chainId` first and SKIP the
    // switch when we're already on the target. Some wallets (Rabby,
    // older MetaMask) treat every `switchChain` call as a UI event and
    // briefly steal focus / surface a "switched to Base" toast even
    // when the network was already correct — adding hundreds of ms of
    // wall-clock delay before the actual signing prompt. The branch
    // here is cheap (one RPC call) and removes that latency for the
    // 99 % case of a user who's already on Base.
    if (!isPrivyEmbedded) {
      try {
        let currentChainIdHex: string | null = null;
        try {
          const raw = await provider.request({ method: 'eth_chainId' });
          if (typeof raw === 'string') currentChainIdHex = raw.toLowerCase();
        } catch {
          // Provider didn't answer — fall through to the unconditional
          // switch path so we never accidentally `eth_sendTransaction`
          // on the wrong chain.
        }
        if (currentChainIdHex === hexChainId) {
          // Already on target — nothing to do.
        } else {
          await wallet.switchChain(chain.id);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isUserRejection =
          msg.includes('rejected') ||
          msg.includes('denied') ||
          msg.includes('User refused') ||
          msg.includes('cancelled') ||
          msg.includes('canceled');
        const friendly = isUserRejection
          ? `Switch your wallet to ${chain.name} to continue`
          : `Failed to switch wallet to ${chain.name}: ${msg}`;
        console.error('[tx] preflight switchChain failed:', err);
        const firstStep = actions[startIndex];
        if (firstStep) {
          updateStep(firstStep.id, { status: 'error', error: friendly });
        }
        setState(prev => ({ ...prev, failedAtIndex: startIndex, isRetrying: false }));
        options?.onError?.(new Error(friendly), firstStep?.id ?? 'wallet');
        return { success: false, results };
      }
    }

    let batchingAvailable = !isPrivyEmbedded && !batchDisabledRef.current;

    // Capability pre-check — ask the wallet whether it can service an atomic
    // batch on this chain BEFORE we issue `wallet_sendCalls`. Wallets that
    // answer "unsupported" (or refuse to answer at all) are routed straight
    // through the sequential path instead of the batch-then-fail-then-retry
    // dance, which is what surfaces N identical KeyringController errors in
    // the snackbar. Probe result is memoised per (wallet, chain) module-side.
    if (batchingAvailable) {
      try {
        const supported = await probeBatchingSupport(provider, walletAddress, hexChainId);
        if (!supported) {
          batchingAvailable = false;
          batchDisabledRef.current = true;
        }
      } catch {
        // Defensive: any unexpected throw from the probe itself shouldn't
        // brick the whole send. Fall back to sequential and continue.
        batchingAvailable = false;
        batchDisabledRef.current = true;
      }
    }

    // Reset steps on retry
    if (isRetry) {
      setState(prev => ({
        ...prev,
        isRetrying: true,
        isBatched: false,
        batchedCount: 0,
        failedAtIndex: undefined,
        steps: prev.steps.map((s, i) =>
          i < startIndex ? s : { ...s, status: 'waiting' as StepStatus, error: undefined, hash: undefined, batched: undefined }
        ),
      }));
    }

    // Run each action, with ERC-5792 batching for consecutive onchain calls
    for (let i = startIndex; i < actions.length; i++) {
      if (cancelledRef.current) {
        for (let j = i; j < actions.length; j++) {
          updateStep(actions[j].id, { status: 'cancelled' });
        }
        setState(prev => ({ ...prev, isRetrying: false }));
        return { success: false, results };
      }

      const action = actions[i];

      // =================================================================
      // ERC-5792 BATCH PATH
      // Collect ALL consecutive onchain calls from the current position,
      // split them into chunks of MAX_BATCH_SIZE, and send each chunk as
      // a separate wallet_sendCalls request. No sequential fallback.
      //
      // Items flagged `noBatch: true` opt out — they break the run boundary
      // and route through the sequential `eth_sendTransaction` path below,
      // so a high-gas step (e.g. V21 `initializeSeats` ~7M gas) doesn't get
      // collected with `atomicRequired: true` into a single bundle that
      // overflows the wallet/bundler/RPC simulation cap (the 256-tile
      // launch failure mode — see use-launch-grid.ts).
      // =================================================================
      if (action.call && !action.noBatch && batchingAvailable) {
        // Collect every consecutive call action starting from i, stopping
        // at the first noBatch boundary so opted-out steps still execute
        // sequentially while neighbouring normal steps batch as before.
        const allCallsRaw: { call: CallOrLazyCall; idx: number }[] = [];
        for (let j = i; j < actions.length && actions[j].call && !actions[j].noBatch; j++) {
          allCallsRaw.push({ call: actions[j].call!, idx: j });
        }
        const runEnd = allCallsRaw[allCallsRaw.length - 1].idx;

        // Split into chunks of MAX_BATCH_SIZE
        const chunks: { call: CallOrLazyCall; idx: number }[][] = [];
        for (let c = 0; c < allCallsRaw.length; c += MAX_BATCH_SIZE) {
          chunks.push(allCallsRaw.slice(c, c + MAX_BATCH_SIZE));
        }

        // Mark ALL items in the run as batched upfront so the snackbar
        // shows the condensed batch view immediately (not after last chunk starts).
        for (const item of allCallsRaw) {
          updateStep(actions[item.idx].id, { batched: true });
        }
        setState(prev => ({ ...prev, isBatched: true, totalBatchChunks: chunks.length }));

        let batchFailed = false;
        for (const chunk of chunks) {
          if (cancelledRef.current) break;

          const chunkStart = chunk[0].idx;
          const chunkEnd = chunk[chunk.length - 1].idx;

          // Mark all chunk steps as pending
          for (const item of chunk) {
            updateStep(actions[item.idx].id, { status: 'pending', error: undefined, batched: true });
          }

          try {
            const batchCalls = await Promise.all(chunk.map(c => resolveCall(c.call)));
            let nonceBefore: number | undefined;
            try {
              nonceBefore = await publicClient.getTransactionCount({ address: walletAddress as `0x${string}` });
            } catch { /* non-critical */ }
            const batchId = await sendCallsBatch(provider, walletAddress, batchCalls);
            const batchResult = await pollCallsStatus(provider, batchId, publicClient, walletAddress, undefined, nonceBefore);

            setState(prev => ({
              ...prev,
              isBatched: true,
              batchedCount: prev.batchedCount + batchCalls.length,
            }));

            for (const item of chunk) {
              updateStep(actions[item.idx].id, {
                status: 'success',
                hash: batchResult.hash,
                batched: true,
              });
              const txResult = { hash: batchResult.hash, receipt: batchResult.receipt };
              results.set(actions[item.idx].id, txResult);
              options?.onStepComplete?.(actions[item.idx].id, txResult);
            }
          } catch (batchError: unknown) {
            const errorMsg = batchError instanceof Error ? batchError.message : String(batchError);
            console.warn(`[ERC-5792] Batch failed (${chunk.length} calls, actions ${chunkStart}-${chunkEnd}):`, batchError);

            // Detect wallets that don't support wallet_sendCalls at all
            const isMethodUnsupported =
              errorMsg.includes("doesn't ha") ||
              errorMsg.includes('not supported') ||
              errorMsg.includes('not found') ||
              errorMsg.includes('does not support') ||
              errorMsg.includes('Method not found') ||
              errorMsg.includes('wallet_sendCalls');

            if (isMethodUnsupported) {
              console.warn('[ERC-5792] Wallet does not support wallet_sendCalls, falling back to sequential execution');
              batchDisabledRef.current = true;
              batchingAvailable = false;

              // Reset ALL batched items back to waiting and fall back to
              // sequential execution from the first item in the run.
              for (const item of allCallsRaw) {
                updateStep(actions[item.idx].id, { status: 'waiting', error: undefined, batched: undefined });
              }
              setState(prev => ({ ...prev, isBatched: false, batchedCount: 0, totalBatchChunks: 0 }));

              // Re-enter the main for-loop at the first call in this run.
              // The for-loop will increment i, so set to idx - 1.
              i = allCallsRaw[0].idx - 1;
              break;
            }

            // Detect MetaMask `KeyringController` / locked-vault errors. These
            // are thrown when the wallet's keyring is in a state that can't
            // service the (often 7702-upgrade-triggering) batch — but the
            // wallet IS reachable and CAN sign individual transactions, so
            // we want to gracefully degrade to the sequential path instead
            // of surfacing N identical opaque "operation cannot be completed"
            // errors in the snackbar (the user-visible bug from the bulk
            // reprice screenshot).
            const isWalletStateError =
              errorMsg.includes('KeyringController') ||
              errorMsg.includes('controller is locked') ||
              errorMsg.includes('vault is locked') ||
              errorMsg.includes('operation cannot be completed');

            if (isWalletStateError) {
              console.warn('[ERC-5792] Wallet keyring/state error — falling back to sequential execution:', errorMsg);
              batchDisabledRef.current = true;
              batchingAvailable = false;

              // Reset ALL batched items back to waiting (same shape as the
              // method-unsupported branch above) so the snackbar doesn't
              // strand N red rows for one underlying wallet-state failure.
              for (const item of allCallsRaw) {
                updateStep(actions[item.idx].id, { status: 'waiting', error: undefined, batched: undefined });
              }
              setState(prev => ({ ...prev, isBatched: false, batchedCount: 0, totalBatchChunks: 0 }));

              // One-shot toast to explain the degraded UX. The user is about
              // to be asked to sign each call individually, which can be a
              // surprise after expecting a single confirmation.
              toast.message('Switching to individual confirmations', {
                description: `Your wallet couldn't sign the batch atomically. You'll be asked to confirm ${allCallsRaw.length} transactions one at a time.`,
              });

              // Re-enter the main for-loop at the first call in this run.
              i = allCallsRaw[0].idx - 1;
              break;
            }

            const isUserRejection =
              errorMsg.includes('rejected') ||
              errorMsg.includes('denied') ||
              errorMsg.includes('cancelled') ||
              errorMsg.includes('canceled') ||
              errorMsg.includes('User refused');

            // Mark this chunk as failed
            for (const item of chunk) {
              updateStep(actions[item.idx].id, {
                status: 'error',
                error: isUserRejection ? 'Transaction rejected' : errorMsg || 'Batch failed',
                batched: undefined,
              });
            }
            // Mark remaining actions as waiting
            for (let k = chunkEnd + 1; k < actions.length; k++) {
              updateStep(actions[k].id, { status: 'waiting' });
            }
            setState(prev => ({ ...prev, failedAtIndex: chunkStart, isRetrying: false }));
            options?.onError?.(
              batchError instanceof Error ? batchError : new Error(errorMsg || 'Batch failed'),
              actions[chunkStart].id,
            );
            batchFailed = true;
            break;
          }
        }

        if (batchFailed) return { success: false, results };

        // If we fell back to sequential, don't skip — the main loop will
        // re-process these calls via the sequential path.
        if (!batchingAvailable) continue;

        // Skip past all batched items (for-loop will increment i)
        i = runEnd;
        continue;
      }

      // =================================================================
      // SEQUENTIAL EXECUTION (standard path / fallback)
      // =================================================================
      updateStep(action.id, { status: 'pending', error: undefined });

      try {
        if (action.action) {
          // ------- Offchain action -------
          const result = await action.action();

          // Action returned a hash needing onchain confirmation
          if (result && typeof result === 'object' && 'hash' in result && 'needsConfirmation' in result && (result as { needsConfirmation: boolean }).needsConfirmation) {
            const { hash } = result as { hash: Hex };
            updateStep(action.id, { hash });
            const receipt = await waitForReceipt(hash, provider);
            if (receipt.status !== 'success') throw new Error('Transaction reverted');
            updateStep(action.id, { status: 'success' });
            const txResult = { hash, receipt };
            results.set(action.id, txResult);
            options?.onStepComplete?.(action.id, txResult);
          }
          // Action returned call data to send as a tx
          else if (result && typeof result === 'object' && 'callData' in result && 'requiresTx' in result) {
            const { callData } = result as { callData: TransactionCall; requiresTx: boolean };
            const { hash, receipt } = await sendAndConfirmTx(callData, provider, walletAddress);
            updateStep(action.id, { hash, status: 'success' });
            results.set(action.id, { hash, receipt });
            options?.onStepComplete?.(action.id, { hash, receipt });
          }
          // Normal async result
          else {
            results.set(action.id, result);
            const resultHash = result && typeof result === 'object' && 'hash' in result
              ? (result as { hash: Hex }).hash
              : undefined;
            updateStep(action.id, { status: 'success', hash: resultHash });
            options?.onStepComplete?.(action.id, result);
          }
        } else if (action.call) {
          // ------- Onchain transaction -------
          const resolvedCall = await resolveCall(action.call);
          const { hash, receipt } = await sendAndConfirmTx(resolvedCall, provider, walletAddress);
          updateStep(action.id, { hash, status: 'success' });
          const txResult = { hash, receipt };
          results.set(action.id, txResult);
          options?.onStepComplete?.(action.id, txResult);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Step failed';
        updateStep(action.id, { status: 'error', error: errorMessage });

        // Mark remaining actions as waiting
        for (let j = i + 1; j < actions.length; j++) {
          updateStep(actions[j].id, { status: 'waiting' });
        }

        setState(prev => ({ ...prev, failedAtIndex: i, isRetrying: false }));
        options?.onError?.(error instanceof Error ? error : new Error(errorMessage), action.id);
        return { success: false, results };
      }
    }

    setState(prev => ({ ...prev, isRetrying: false, failedAtIndex: undefined }));
    options?.onSuccess?.(results);
    refreshAllBalances();
    return { success: true, results };
  }, [
    wallet,
    isPrivyEmbedded,
    getEip1193Provider,
    publicClient,
    updateStep,
    refreshAllBalances,
    waitForReceipt,
    sendAndConfirmTx,
    resolveCall,
  ]);

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  const cancelQueue = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const executeActions = useCallback(async (
    title: string,
    actions: ActionQueueItem[],
    options?: ExecuteOptions
  ): Promise<{ success: boolean; results: Map<string, unknown> }> => {
    cancelledRef.current = false;
    currentActionsRef.current = actions;
    currentOptionsRef.current = options;
    currentResultsRef.current = new Map();

    console.info('[tx] executeActions:', {
      title,
      stepCount: actions.length,
      walletAddress: wallet?.address ?? null,
      walletClientType: wallet?.walletClientType ?? null,
      hasConnectorClient: !!connectorClient,
    });

    if (!wallet) {
      console.warn('[tx] executeActions aborted: no wallet connected');
      options?.onError?.(new Error('No wallet connected'), 'wallet');
      return { success: false, results: new Map() };
    }

    setState({
      isOpen: true,
      title,
      steps: actions.map(a => ({ id: a.id, label: a.label, description: a.description, status: 'waiting' })),
      isBatched: false,
      batchedCount: 0,
      totalBatchChunks: 0,
      isRetrying: false,
      failedAtIndex: undefined,
    });

    return executeFromIndex(0, false);
  }, [wallet, connectorClient, executeFromIndex]);

  const retryFromFailed = useCallback(async () => {
    cancelledRef.current = false;
    const idx = state.failedAtIndex;
    if (idx === undefined) return { success: false, results: currentResultsRef.current };
    return executeFromIndex(idx, true);
  }, [state.failedAtIndex, executeFromIndex]);

  const retryAll = useCallback(async () => {
    cancelledRef.current = false;
    currentResultsRef.current = new Map();
    return executeFromIndex(0, true);
  }, [executeFromIndex]);

  const close = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const canRetry = state.failedAtIndex !== undefined && !state.isRetrying;

  const value: ActionQueueContextValue = {
    state,
    supportsBatching: !isPrivyEmbedded && !batchDisabledRef.current,
    executeActions,
    retryFromFailed,
    retryAll,
    cancelQueue,
    canRetry,
    close,
    updateStep,
    prefetchProvider,
  };

  return (
    <ActionQueueContext.Provider value={value}>
      {children}
    </ActionQueueContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useTransactionQueue() {
  const context = useContext(ActionQueueContext);
  if (!context) {
    throw new Error('useTransactionQueue must be used within a TransactionQueueProvider');
  }
  return context;
}
